import type {
  ContentDocumentDetail,
  ContentDocumentListPage,
  ContentDocumentSummary,
  ContentTypeName,
  JsonObject,
  JsonValue,
} from "@content-types"
import { contentLocaleSchema, contentStatusSchema, contentTypeNameSchema } from "@content-types"
import { createServerFn } from "@tanstack/react-start"
import * as z from "zod"
import { requirePlaybrainWebsitePermission } from "../dashboard-permissions.server"
import { requireDashboardSession } from "../dashboard-server-auth"

const listInputSchema = z.object({
  language: contentLocaleSchema.optional(),
  page: z.number().int().positive().optional(),
  perPage: z.number().int().positive().max(100).optional(),
  type: contentTypeNameSchema.optional(),
  status: contentStatusSchema.optional(),
  query: z.string().optional(),
  teamKey: z.string().trim().min(1).optional(),
})

const documentInputSchema = z.object({
  id: z.string(),
})

const createDraftInputSchema = z.object({
  type: contentTypeNameSchema,
  language: z.enum(["en", "ja"]).optional(),
  values: z.record(z.string(), z.unknown()).optional(),
})

const saveDraftInputSchema = z.object({
  expectedUpdatedAt: z.string().optional(),
  id: z.string(),
  values: z.record(z.string(), z.unknown()),
})

const saveDocumentInputSchema = z.object({
  expectedUpdatedAt: z.string().optional(),
  id: z.string().optional(),
  status: contentStatusSchema,
  type: contentTypeNameSchema,
  values: z.record(z.string(), z.unknown()),
})

const createCareerTeamInputSchema = z.object({
  name: z.string().trim().min(1),
})

const deleteCareerTeamInputSchema = z.object({
  key: z.string().trim().min(1),
})

type ListInput = z.infer<typeof listInputSchema>
type DocumentInput = z.infer<typeof documentInputSchema>

export type { ContentDocumentDetail, ContentDocumentListPage, ContentDocumentSummary, JsonObject, JsonValue }

export const listContentDocuments = createServerFn({ method: "GET" })
  .validator((input: unknown) => listInputSchema.parse(input ?? {}))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requirePlaybrainWebsitePermission(user, requireContentType(data.type), "read")
    const { listContentDocumentsData } = await import("@api/features/content-records/cmsRecords")
    return listContentDocumentsData(data as ListInput)
  })

export const getContentDocument = createServerFn({ method: "GET" })
  .validator((input: unknown) => documentInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    const { getContentDocumentData } = await import("@api/features/content-records/cmsRecords")
    const document = await getContentDocumentData(data as DocumentInput)
    await requirePlaybrainWebsitePermission(user, document.type, "read")
    return document
  })

export const createContentDraft = createServerFn({ method: "POST" })
  .validator((input: unknown) => createDraftInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requirePlaybrainWebsitePermission(user, data.type, "create")
    const { createContentDraftData } = await import("@api/features/content-records/cmsRecords")
    return createContentDraftData(data)
  })

export const saveContentDraft = createServerFn({ method: "POST" })
  .validator((input: unknown) => saveDraftInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    const { getContentDocumentData, saveContentDraftData } = await import(
      "@api/features/content-records/cmsRecords"
    )
    const document = await getContentDocumentData({ id: data.id })
    await requirePlaybrainWebsitePermission(user, document.type, "write")
    return saveContentDraftData(data)
  })

export const saveContentDocument = createServerFn({ method: "POST" })
  .validator((input: unknown) => saveDocumentInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requirePlaybrainWebsitePermission(
      user,
      data.type,
      data.status === "published" ? "publish" : data.id ? "write" : "create"
    )
    const { saveContentDocumentData } = await import("@api/features/content-records/cmsRecords")
    return saveContentDocumentData(data)
  })

export const publishContentDraft = createServerFn({ method: "POST" })
  .validator((input: unknown) => documentInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    const { getContentDocumentData, publishContentDraftData } = await import(
      "@api/features/content-records/cmsRecords"
    )
    const document = await getContentDocumentData(data as DocumentInput)
    await requirePlaybrainWebsitePermission(user, document.type, "publish")
    return publishContentDraftData(data)
  })

export const moveContentDocumentToDraft = createServerFn({ method: "POST" })
  .validator((input: unknown) => documentInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    const { getContentDocumentData, moveContentDocumentToDraftData } = await import(
      "@api/features/content-records/cmsRecords"
    )
    const document = await getContentDocumentData(data as DocumentInput)
    await requirePlaybrainWebsitePermission(user, document.type, "write")
    return moveContentDocumentToDraftData(data)
  })

export const archiveContentDocument = createServerFn({ method: "POST" })
  .validator((input: unknown) => documentInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    const { archiveContentDocumentData, getContentDocumentData } = await import(
      "@api/features/content-records/cmsRecords"
    )
    const document = await getContentDocumentData(data as DocumentInput)
    await requirePlaybrainWebsitePermission(user, document.type, "archive")
    return archiveContentDocumentData(data)
  })

export const deleteContentDocument = createServerFn({ method: "POST" })
  .validator((input: unknown) => documentInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    const { deleteContentDocumentData, getContentDocumentData } = await import(
      "@api/features/content-records/cmsRecords"
    )
    const document = await getContentDocumentData(data as DocumentInput)
    await requirePlaybrainWebsitePermission(user, document.type, "delete")
    return deleteContentDocumentData(data)
  })

export const listCareerTeams = createServerFn({ method: "GET" })
  .validator((input: unknown) => z.object({}).parse(input ?? {}))
  .handler(async () => {
    const user = await requireDashboardSession()
    await requirePlaybrainWebsitePermission(user, "careers", "read")
    const { listCareerTeamsData } = await import("@api/features/content-records/cmsRecords")
    return listCareerTeamsData()
  })

export const createCareerTeam = createServerFn({ method: "POST" })
  .validator((input: unknown) => createCareerTeamInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requirePlaybrainWebsitePermission(user, "careers", "create")
    const { createCareerTeamData } = await import("@api/features/content-records/cmsRecords")
    return createCareerTeamData(data)
  })

export const deleteCareerTeam = createServerFn({ method: "POST" })
  .validator((input: unknown) => deleteCareerTeamInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requirePlaybrainWebsitePermission(user, "careers", "delete")
    const { deleteCareerTeamData } = await import("@api/features/content-records/cmsRecords")
    return deleteCareerTeamData(data)
  })

function requireContentType(type: ContentTypeName | undefined): ContentTypeName {
  if (!type) throw new Error("Content type is required for permission checks.")
  return type
}
